import { Checkbox, Currency, Form } from '@mairistem/composants';
import { MULTI } from '@mairistem/comptabilite';
import { Date, Panel } from '@mairistem/finances-utils';
import React from 'react';
import type PanelComponentProps from '../../../interfaces/pieces/mouvements/panelComponentProps';
import './DetailDeFacturationComponent.css';

interface ModeAvanceProps {
	lfacMtunitaire: number | string;
	lfacQuantite: number | string;
	lfacUnite1: string;
}

const customFloatRegex = /^\d{0,6}(?:[.,]\d{0,4})?$/;

const LIGNE_FACTURE_MT_BASE = 'lfacMtbase';

const DetailDeFacturationComponent = ({
	data,
	handlePin,
	onChange,
	panel,
}: PanelComponentProps) => {
	const [isModeAvance, setIsModeAvance] = React.useState<boolean>(false);
	const [modeAvanceData, setModeAvanceData] = React.useState<ModeAvanceProps>({
		lfacMtunitaire: null,
		lfacQuantite: null,
		lfacUnite1: null,
	});

	// Si les valeurs du mode avancé on été inscrites alors la checkbox est cochée : mode avancé activé
	React.useEffect(() => {
		if (
			modeAvanceData?.lfacMtunitaire !== data?.lfacMtunitaire ||
			modeAvanceData?.lfacQuantite !== data?.lfacQuantite ||
			modeAvanceData?.lfacUnite1 !== data?.lfacUnite1
		) {
			// Si une des valeurs est changée, on ajoute au state
			setModeAvanceData({
				lfacMtunitaire: data?.lfacMtunitaire,
				lfacQuantite: data?.lfacQuantite,
				lfacUnite1: data?.lfacUnite1,
			});
			setIsModeAvance(
				data?.lfacMtunitaire > 0 ||
					data?.lfacQuantite > 0 ||
					data?.lfacQuantite === MULTI ||
					data?.lfacUnite1?.length > 0,
			);
		}
	}, [
		data,
		modeAvanceData?.lfacMtunitaire,
		modeAvanceData?.lfacQuantite,
		modeAvanceData?.lfacUnite1,
	]);

	const handleChangeCustomFloatValues = (_e, { name, value }) => {
		if (customFloatRegex?.test(value)) {
			onChange?.({ [name]: value }, panel);
			setModeAvanceData((old) => ({ ...old, [name]: value }));
		}
	};

	const handleChangeUnite = (_e, { name, value }) => {
		onChange?.({ [name]: value }, panel);
		setModeAvanceData((old) => ({ ...old, [name]: value }));
	};

	const handleToggle = (_e, { checked }) => {
		setIsModeAvance(checked);
		if (!checked) {
			onChange?.(
				{
					lfacQuantite: null,
					lfacMtunitaire: null,
					lfacUnite1: null,
				},
				panel,
			);
		}
	};

	const handleChange = (_e, { name, value }) =>
		onChange?.({ [name]: value }, panel);

	const handleChangeDate = (value: string, name: string) =>
		onChange({ [name]: value }, panel);

	const handleChangeMontant = (_e, value) =>
		onChange?.({ [LIGNE_FACTURE_MT_BASE]: value }, panel);

	return (
		<Panel panel={panel} onPin={handlePin}>
			<Form>
				<Form.Group>
					<Form.Field
						control={Date}
						data-testid="detailDeFacturationDateDebut"
						disabled={data?.lfacDate === MULTI}
						fluid
						key="lfacDate"
						label="Date de début"
						name="lfacDate"
						onChange={handleChangeDate}
						value={data?.lfacDate}
						width={7}
					/>
					<Form.Field
						control={Date}
						data-testid="detailDeFacturationDatefin"
						disabled={data?.lfacDatefin === MULTI}
						fluid
						key="lfacDatefin"
						label="Date de fin"
						name="lfacDatefin"
						onChange={handleChangeDate}
						value={data?.lfacDatefin}
						width={7}
					/>
					<Form.Field
						className="modeAvance"
						control={Checkbox}
						toggle
						checked={isModeAvance ?? false}
						width={2}
						data-testid="detailDeFacturationCheckbox"
						onChange={handleToggle}
					/>
				</Form.Group>
				<Form.Input
					label="Libellé de la prestation"
					onChange={handleChange}
					name="lfacLibelle"
					key="lfacLibelle"
					value={data?.lfacLibelle !== MULTI ? data?.lfacLibelle : ''}
					placeholder={data?.lfacLibelle}
					fluid
					maxLength={200}
					data-testid="detailDeFacturationLibelleInput"
				/>
				{isModeAvance ? (
					<Form.Group>
						<Form.Input
							label="Quantité"
							onChange={handleChangeCustomFloatValues}
							disabled={data?.lfacQuantite === MULTI}
							name="lfacQuantite"
							key="lfacQuantite"
							value={data?.lfacQuantite !== MULTI ? data?.lfacQuantite : ''}
							width={6}
							maxLength={7}
							data-testid="detailDeFacturationQuantiteInput"
						/>
						<Form.Input
							label="Unité"
							onChange={handleChangeUnite}
							name="lfacUnite1"
							key="lfacUnite1"
							disabled={data?.lfacUnite1 === MULTI}
							value={data?.lfacUnite1 !== MULTI ? data?.lfacUnite1 : ''}
							width={4}
							maxLength={5}
							data-testid="detailDeFacturationUniteInput"
						/>
						<Form.Input
							label="Prix unitaire"
							onChange={handleChangeCustomFloatValues}
							name="lfacMtunitaire"
							key="lfacMtunitaire"
							value={data?.lfacMtunitaire !== MULTI ? data?.lfacMtunitaire : ''}
							disabled={data?.lfacMtunitaire === MULTI}
							width={6}
							maxLength={7}
							data-testid="detailDeFacturationPrixUnitaireInput"
						/>
					</Form.Group>
				) : (
					<Currency
						label="Montant"
						onBlur={handleChangeMontant}
						placeholder={data?.lfacMtbase}
						value={data?.lfacMtbase !== MULTI ? data?.lfacMtbase : 0}
						disabled={data?.lfacMtbase === MULTI}
						name={LIGNE_FACTURE_MT_BASE}
						key={LIGNE_FACTURE_MT_BASE}
						fluid
						maxLength={14}
						precision={2}
						valueMax={999999}
						data-testid="detailDeFacturationMontantInput"
					/>
				)}
			</Form>
		</Panel>
	);
};

export default DetailDeFacturationComponent;
